import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import api from '../config/api';
import moment from 'moment';
import { shareKitchenCopyWhatsApp } from '../utils/kitchenShareWhatsApp';
import { sendOrderDetailsWhatsApp } from '../utils/sendOrderWhatsApp';
import PageNavbar from './PageNavbar';
import { formatDateDisplay } from '../utils/formatDate';
import './Orders.css';

const CATEGORIES = ['all', 'Online Orders', 'Events', 'N Caterers', 'Store-1', 'Store-2', 'Desserts', 'Catering'];
const STATUS_FILTERS = ['all', 'Pending', 'In Progress', 'Ready', 'Delivered', 'Cancelled'];

const Orders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateFromUrl = searchParams.get('date');
  const categoryFromUrl = searchParams.get('category');
  const initialDate =
    dateFromUrl && moment(dateFromUrl, 'YYYY-MM-DD', true).isValid()
      ? moment(dateFromUrl, 'YYYY-MM-DD').toDate()
      : new Date();

  const [orders, setOrders] = useState([]);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(
    categoryFromUrl && CATEGORIES.includes(categoryFromUrl) ? categoryFromUrl : 'all'
  );
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState('');
  const [searching, setSearching] = useState(false);
  const [sharingKitchenOrderId, setSharingKitchenOrderId] = useState(null);
  const [sendingCustomerOrderId, setSendingCustomerOrderId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (dateFromUrl && moment(dateFromUrl, 'YYYY-MM-DD', true).isValid()) {
      setSelectedDate(moment(dateFromUrl, 'YYYY-MM-DD').toDate());
    }
  }, [dateFromUrl]);

  useEffect(() => {
    if (categoryFromUrl && CATEGORIES.includes(categoryFromUrl)) {
      setFilter(categoryFromUrl);
    }
  }, [categoryFromUrl]);

  const handleShareKitchenFromList = async (order, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSharingKitchenOrderId(order.id);
    try {
      await shareKitchenCopyWhatsApp(order, order.id);
    } finally {
      setSharingKitchenOrderId(null);
    }
  };

  const handleSendCustomerFromList = async (order, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!order.customer_phone) {
      alert('This order has no customer phone number.');
      return;
    }
    if (!window.confirm(`Send order details on WhatsApp to ${order.customer_phone}?`)) {
      return;
    }
    setSendingCustomerOrderId(order.id);
    try {
      const data = await sendOrderDetailsWhatsApp(order.id);
      alert(data?.message || `Order details sent to ${data?.mobile || order.customer_phone}`);
    } catch (error) {
      console.error('Send customer WhatsApp:', error);
      alert(error.response?.data?.error || error.message || 'Failed to send WhatsApp message');
    } finally {
      setSendingCustomerOrderId(null);
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const dateStr = moment(selectedDate).format('YYYY-MM-DD');
      const params = { deliveryDate: dateStr };
      if (filter !== 'all') params.category = filter;

      const response = await api.get('/orders', { params });
      const ordersData = response.data?.orders ?? [];
      setOrders(Array.isArray(ordersData) ? ordersData : []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, filter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const next = new URLSearchParams(searchParams);
    next.set('date', moment(date).format('YYYY-MM-DD'));
    setSearchParams(next, { replace: true });
  };

  const handleCategoryChange = (cat) => {
    setFilter(cat);
    const next = new URLSearchParams(searchParams);
    if (cat === 'all') next.delete('category');
    else next.set('category', cat);
    setSearchParams(next, { replace: true });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchError('Please enter an order number');
      return;
    }

    setSearching(true);
    setSearchError('');

    try {
      const response = await api.get(`/orders/by-code/${searchQuery.trim()}`);
      if (response.data?.orderId) {
        navigate(`/orders/${response.data.orderId}`);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setSearchError(`Order "${searchQuery}" not found`);
      } else {
        setSearchError('Error searching for order');
      }
    } finally {
      setSearching(false);
    }
  };

  const getProductDescription = (order) => {
    const items = order.items || [];
    if (items.length === 0) return null;
    const descs = items.map((item) => {
      if (item.item_type === 'Cakes' && item.product_name) {
        return item.flavour ? `${item.product_name} (${item.flavour})` : item.product_name;
      }
      if (item.item_type === 'Desserts' && item.dessert_name) return item.dessert_name;
      if (item.item_type === 'Grazing Table') return item.custom_item_name || 'Grazing';
      if (item.item_type === 'Cup Cakes' && item.cup_cake_name) return item.cup_cake_name;
      if (item.item_type === 'Plants' && item.plant_name) return item.plant_name;
      if (item.item_type === 'Snack Boxes' && item.snack_box_items?.length) {
        const names = item.snack_box_items.map((s) => `${s.item_name} x${s.quantity}`).join(', ');
        return `Snack Box: ${names}`;
      }
      return item.item_type;
    });
    return descs.join('; ');
  };

  const filteredOrders = useMemo(() => {
    if (statusFilter === 'all') return orders;
    return orders.filter((o) => o.status === statusFilter);
  }, [orders, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: orders.length };
    STATUS_FILTERS.slice(1).forEach((s) => {
      counts[s] = orders.filter((o) => o.status === s).length;
    });
    return counts;
  }, [orders]);

  return (
    <div className="orders-page">
      <PageNavbar />
      <div className="container">
        <div className="orders-header">
          <div className="orders-header__titles">
            <h1>Orders</h1>
            <p className="orders-header__lead">
              Pick a date, filter by category or status, then open an order.
            </p>
          </div>
          <div className="header-actions">
            <form onSubmit={handleSearch} className="search-form">
              <input
                type="text"
                className="form-control search-input"
                placeholder="Order no. e.g. CX-0001"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchError('');
                }}
              />
              <button type="submit" className="btn btn-secondary" disabled={searching}>
                {searching ? 'Searching…' : 'Search'}
              </button>
            </form>
            <Link to="/orders/create" className="btn btn-primary">
              Create order
            </Link>
          </div>
        </div>
        {searchError && <div className="search-error">{searchError}</div>}

        <div className="orders-layout">
          <aside className="calendar-section" aria-label="Date and category">
            <h2 className="orders-side-title">Delivery date</h2>
            <Calendar
              onChange={handleDateChange}
              value={selectedDate}
              className="calendar"
            />
            <div className="filter-section">
              <span className="filter-section__label">Category</span>
              <div className="orders-chip-row" role="listbox" aria-label="Category filter">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    role="option"
                    aria-selected={filter === cat}
                    className={`orders-chip${filter === cat ? ' is-active' : ''}`}
                    onClick={() => handleCategoryChange(cat)}
                  >
                    {cat === 'all' ? 'All' : cat}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <div className="orders-list">
            <div className="orders-list__toolbar">
              <div>
                <h2>{formatDateDisplay(selectedDate)}</h2>
                <p className="orders-list__count">
                  {loading
                    ? 'Loading…'
                    : `${filteredOrders.length} order${filteredOrders.length === 1 ? '' : 's'}${
                        statusFilter !== 'all' ? ` · ${statusFilter}` : ''
                      }`}
                </p>
              </div>
              <div className="orders-chip-row orders-chip-row--status" aria-label="Status filter">
                {STATUS_FILTERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`orders-chip orders-chip--soft${statusFilter === s ? ' is-active' : ''}`}
                    onClick={() => setStatusFilter(s)}
                  >
                    {s === 'all' ? `All (${statusCounts.all || 0})` : `${s} (${statusCounts[s] || 0})`}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading orders…</div>
            ) : filteredOrders.length === 0 ? (
              <div className="no-orders">No orders for this date and filters</div>
            ) : (
              <div className="orders-table-wrap">
                <ul className="orders-rows">
                  {filteredOrders.map((order) => {
                    const isCancelled = String(order.status || '').toLowerCase() === 'cancelled';
                    const product = getProductDescription(order);
                    return (
                      <li
                        key={order.id}
                        className={`orders-row${isCancelled ? ' orders-row--cancelled' : ''}`}
                      >
                        <button
                          type="button"
                          className="orders-row__main"
                          onClick={() => navigate(`/orders/${order.id}`)}
                        >
                          <div className="orders-row__id">
                            <strong>{order.order_number}</strong>
                            <span className={`order-status status-${String(order.status || '').toLowerCase().replace(' ', '-')}`}>
                              {order.status}
                            </span>
                          </div>
                          <div className="orders-row__meta">
                            <span className="orders-row__customer">{order.customer_name || '—'}</span>
                            <span className="orders-row__cat">{order.category}</span>
                            <span className="orders-row__time">{order.delivery_time || '—'}</span>
                            <span className="orders-row__total">₹{order.total_amount}</span>
                          </div>
                          {product ? (
                            <p className="orders-row__products" title={product}>
                              {product}
                            </p>
                          ) : null}
                        </button>
                        <div className="orders-row__actions">
                          <button
                            type="button"
                            className="btn btn-sm orders-row__wa"
                            disabled={sendingCustomerOrderId === order.id || !order.customer_phone}
                            title={!order.customer_phone ? 'No customer phone' : 'WhatsApp customer'}
                            onClick={(e) => handleSendCustomerFromList(order, e)}
                          >
                            {sendingCustomerOrderId === order.id ? '…' : 'Customer WA'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm orders-row__wa orders-row__wa--kitchen"
                            disabled={sharingKitchenOrderId === order.id}
                            onClick={(e) => handleShareKitchenFromList(order, e)}
                          >
                            {sharingKitchenOrderId === order.id ? '…' : 'Kitchen WA'}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Orders;
